import { useEffect, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

/**
 * B2B tenant guard for every `/c/[slug]/*` route.
 *
 * Blocks a logged-in user from viewing another pub's branded environment
 * (branding, predictions, standings). Rules — enforced by the backend
 * `/api/auth/tenant-access/{slug}` endpoint:
 *
 *   • Super-Admin (`role="admin"`) → allowed for any slug.
 *   • Client (`role="client"`) → allowed if the target pub belongs to
 *     their client_id.
 *   • Regular user → allowed only if `pub_id` matches the target pub.
 *
 * When denied, we redirect the user back to their OWN branded env
 * (`/c/[correct_slug]`) — or the platform root if they have none. The
 * unauth case is intentionally handled by the child `ProtectedRoute`
 * (or by `ClientSlugHome` for bare `/c/[slug]`) — this guard only
 * fires once a user is bootstrapped.
 *
 * Fail-safe: while the async check is inflight we render a lightweight
 * loading state — better than flashing the wrong pub's branded chrome.
 */
export function TenantGuard({ children }) {
  const { slug } = useParams();
  const { user, bootstrapped } = useAuth();
  // states: "checking" | "allowed" | { redirectTo: string }
  const [state, setState] = useState("checking");

  useEffect(() => {
    // Not bootstrapped or unauth → don't fire the guard. The auth flow
    // (ProtectedRoute / SluggedLoginPage) will handle redirection.
    if (!bootstrapped || !user || !slug) {
      setState("allowed");
      return;
    }
    // Fast-path — super-admin. Skip the round-trip entirely.
    if (user.role === "admin") {
      setState("allowed");
      return;
    }
    // iter88 — Editors (is_editor=true) are platform content operators
    // and always allowed on any slug (mirrors backend `_require_client`
    // + tenant-access rules). Skip the round-trip so the guard doesn't
    // flash a Loading state on every editor navigation.
    if (user.is_editor) {
      setState("allowed");
      return;
    }
    // Fast-path — regular user with a matching pub_slug already known
    // from /auth/me. Avoids an extra network hop on every route change.
    // iter152 — also accept `client_slug` (denormalized) as a fallback,
    // for users whose pub_slug wasn't stamped by an older assignment
    // endpoint. Either matching the URL slug counts as "same tenant".
    const knownSlug = user.pub_slug || user.client_slug;
    if (user.role !== "client" && knownSlug === slug) {
      setState("allowed");
      return;
    }
    // Everything else needs the authoritative server check (client-role
    // ownership of multiple pubs, mismatched pub_slug, etc.).
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/auth/tenant-access/${encodeURIComponent(slug)}`);
        if (cancelled) return;
        if (data?.allowed) {
          setState("allowed");
        } else {
          setState({
            redirectTo: data?.correct_slug ? `/c/${data.correct_slug}` : "/",
          });
        }
      } catch {
        if (cancelled) return;
        // On any error (network, 401, 500) fail closed — send the user
        // home rather than potentially leaking another pub's chrome.
        setState({ redirectTo: user.pub_slug ? `/c/${user.pub_slug}` : "/" });
      }
    })();
    return () => { cancelled = true; };
  }, [slug, user, bootstrapped]);

  if (state === "checking") {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-white"
        data-testid="tenant-guard-loading"
      >
        <div className="text-sm uppercase tracking-[0.2em] text-zinc-500">Loading…</div>
      </div>
    );
  }
  if (typeof state === "object" && state.redirectTo) {
    return <Navigate to={state.redirectTo} replace />;
  }
  return children;
}
